import {useFormik} from 'formik'
import MuiPhoneNumber from 'material-ui-phone-number'
import React, {useState} from 'react'
import DatePicker from 'react-datepicker'
import AadharCardUpload from './AadharCardUpload'
import CompanyIDUpload from './CompanyIDUpload'
import Swal from 'sweetalert2'
import {confirmLeadBooking} from '../core/_requests'
import * as Yup from 'yup'

export interface uploadCardDataType {
  front: string
  back: string
}
export interface aadharCardDetailsDataType {
  person: number
  side: string
}
export interface companyIDDetailsDataType {
  ID: number
  side: string
}

interface leadsDetailsFormDataType {
  id: string
  closeForm?: () => void
  getLeadList?: () => void
  setSuccessfullySubmitted: (successfullySubmitted: boolean) => void
  outsideForm: boolean
  data?: any // Make the data property optional
}
const LeadsDetailsForm = ({
  id,
  closeForm,
  getLeadList,
  setSuccessfullySubmitted,
  outsideForm,
  data,
}: leadsDetailsFormDataType) => {
  const [submitBtnLoading, setSubmitBtnLoading] = useState<boolean>(false)
  const [moveInDate, setMoveInDate] = useState<Date | null>(null)
  const [rentAgreementSigningDate, setRentAgreementSigningDate] = useState<Date | null>(null)
  const [uploadAadharCard, setUploadAadharCard] = useState<uploadCardDataType[]>([])
  const [aadharDetails, setAadharDetails] = useState<aadharCardDetailsDataType>({
    person: 1,
    side: 'front',
  })
  const [uploadCompanyID, setUploadCompanyID] = useState<uploadCardDataType[]>([])
  const [companyIDDetails, setCompanyIDDetails] = useState<companyIDDetailsDataType>({
    ID: 1,
    side: 'front',
  })

  // Initial values for formik form
  const initialValues = {
    lead_property_booking_uuid: '',
    fullName: data?.full_name || '', // required
    addressLine1: data?.address_1 || '', // required
    addressLine2: data?.address_2 || '',
    city: data?.city || '',
    pincode: data?.pincode || '', // required
    mobileNumber: data?.phone_number || '', // required
    email: data?.email || '',
    moveInDate: '',
    depositAmount: '', // required
    monthlyRent: '', // required
    rentAgreementSigningDate: '',
    companyName: '',
    companyIdImage: '',
    aadhaarCard: '', // required, can be multiple
    isPayingNOCCharge: '',
    isPayingLegalCharge: '',
    NOCCharge: '',
    legalCharge: '',
    administrationCharges: '',
    brokerageCost: '',
  }

  // function to submit the confirm booking form onSubmit
  const handleConfirmBooking = async (values: any) => {
    setSubmitBtnLoading(true)
    try {
      const {data: res} = await confirmLeadBooking(
        id,
        values.fullName, // required
        values.addressLine1, // required
        values.pincode, // required
        values.mobileNumber && values.mobileNumber.split(' ')[1].replace('-', ''), // required
        values.depositAmount, // required
        values.monthlyRent, // required
        uploadAadharCard, // required, can be multiple
        values.addressLine2,
        values.city,
        values.email,
        values.moveInDate,
        values.rentAgreementSigningDate,
        values.companyName,
        uploadCompanyID.length > 0 ? uploadCompanyID[0] : '',
        values.isPayingNOCCharge === '' ? '' : values.isPayingNOCCharge === 'true' ? true : false,
        values.isPayingLegalCharge === ''
          ? ''
          : values.isPayingLegalCharge === 'true'
          ? true
          : false,
        parseFloat(values.NOCCharge),
        parseFloat(values.legalCharge),
        parseFloat(values.administrationCharges),
        parseFloat(values.brokerageCosts)
      )
      if (res.code === '200') {
        setSuccessfullySubmitted(true)
        // if (outsideForm && setSuccessfullySubmitted) {
        //   // setSuccessfullySubmitted(true)
        // } else
        if (closeForm && getLeadList) {
          // closeForm()
          getLeadList()
        }
        formik.resetForm()
        setUploadAadharCard([])
        setUploadCompanyID([])
        setRentAgreementSigningDate(null)
        setMoveInDate(null)
        setAadharDetails({person: 1, side: 'front'})
        setCompanyIDDetails({ID: 1, side: 'front'})
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
          text: res.msg_details,
          allowOutsideClick: true,
        })
      }
    } catch (error: any) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: error,
        allowOutsideClick: true,
      })
    } finally {
      setSubmitBtnLoading(false)
    }
  }

  // validation for the formik form
  const validationSchema = Yup.object().shape({
    aadhaarCard: Yup.array()
      .of(
        Yup.object().shape({
          front: Yup.string().required('Front side of aadhar card is required'),
          back: Yup.string().required('Back side of aadhar card is required'),
        })
      )
      .required('Aadhar card is required'),
    fullName: Yup.string()
      .required('Full name is required')
      .min(3, 'Minimum 3 characters required')
      .max(50, 'Maximum 50 characters are allowed'), // required
    addressLine1: Yup.string()
      .min(3, 'Minimum 3 characters required')
      .max(600, 'Maximum 600 characters are allowed')
      .required('Address is required'),
    addressLine2: Yup.string()
      .min(3, 'Minimum 3 characters required')
      .max(600, 'Maximum 600 characters are allowed')
      .optional(),
    city: Yup.string()
      .min(3, 'Minimum 3 characters required')
      .max(100, 'Maximum 100 characters are allowed')
      .optional(),
    pincode: Yup.number()
      .typeError('Pincode must be a number')
      .positive('Pincode must be a positive number')
      .integer('Pincode must be an integer')
      .min(100000, 'Pincode must be at least 6 digits')
      .max(999999, 'Pincode cannot be more than 6 digits')
      .required('Pincode is required'),
    mobileNumber: Yup.string().required('Phone number is required'), // required
    email: Yup.string().email('Wrong email format').optional(),
    moveInDate: Yup.string().optional(),
    depositAmount: Yup.number()
      .typeError('Deposit amount must be a number')
      .positive('Deposit amount must be a positive number')
      .integer('Deposit amount must be an integer')
      .required('Deposit amount is required'), // required
    monthlyRent: Yup.number()
      .typeError('Monthly rent must be a number')
      .positive('Monthly rent must be a positive number')
      .integer('Monthly rent must be an integer')
      .required('Monthly rent is required'), // required
    rentAgreementSigningDate: Yup.string().required('Rent agreement signing date is required'),
    companyName: Yup.string()
      .min(3, 'Minimum 3 characters required')
      .max(600, 'Maximum 600 characters are allowed')
      .optional(),
    isPayingNOCCharge: Yup.string().optional(),
    isPayingLegalCharge: Yup.string().optional(),
    NOCCharge: Yup.number()
      .typeError('NOC Charge field must be a number')
      .when('isPayingNOCCharge', {
        is: 'true',
        then: Yup.number().required('NOC Charge field is required'),
        otherwise: Yup.number().notRequired(),
      })
      .min(0, 'Negative values are not allowed'),

    legalCharge: Yup.number()
      .typeError('Legal charge field must be a number')
      .when('isPayingLegalCharge', {
        is: 'true',
        then: Yup.number().required('Legal charge field is required'),
        otherwise: Yup.number().notRequired(),
      })
      .min(0, 'Negative values are not allowed'),

    administrationCharges: Yup.number()
      .typeError('Administration charge field must be a number')
      .min(0, 'Negative values are not allowed'),
    brokerageCost: Yup.number()
      .typeError('Bokerage cost field must be a number')
      .min(0, 'Negative values are not allowed'),
  })

  // Formik object for the formik form
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      handleConfirmBooking(values)
    },
  })
  console.log('errors33', formik.errors)

  // function to format the date
  const formatDate = (date: Date) => {
    const formattedDate = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    return formattedDate
  }
  return (
    <form onSubmit={formik.handleSubmit} className='px-10 mb-0 pt-5'>
      {/* Begin: form fields*/}
      <div className='row w-100'>
        <div className='w-100'>
          {/*Begin::Form Group Full Name */}
          <div className='col-12 col-lg-6 mb-2'>
            <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
              <span className='required'>Full Name</span>
              <i
                className='fas fa-exclamation-circle ms-2 fs-7'
                data-bs-toggle='tooltip'
                title='Enter full Name'
              ></i>
            </label>
            <input
              type='text'
              className='form-control form-control-lg form-control-solid'
              placeholder='Enter full Name'
              {...formik.getFieldProps('fullName')}
              name='fullName'
            />
            <div className='h-20px'>
              {formik.touched.fullName && formik.errors.fullName && (
                <div className='fv-help-block color-red'>{String(formik.errors.fullName)}</div>
              )}
            </div>
          </div>
          {/*End::Form Group Full Name */}
        </div>
        {/*Begin::Form Group Phone Number */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>Phone Number</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter phone number'
            ></i>
          </label>
          <MuiPhoneNumber
            {...formik.getFieldProps('mobileNumber')}
            className='w-100'
            defaultCountry={'in'}
            onChange={(value) => formik.setFieldValue('mobileNumber', value)}
          />
          <div className='h-20px'>
            {formik.touched.mobileNumber && formik.errors.mobileNumber && (
              <div className='fv-help-block color-red'>{String(formik.errors.mobileNumber)}</div>
            )}
          </div>
        </div>
        {/*End::Form Group Phone Number */}
        {/*Begin::Form Group email address */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Email Address</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter email address'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter email address'
            {...formik.getFieldProps('email')}
            name='email'
          />
          <div className='h-20px'>
            {formik.touched.email && formik.errors.email && (
              <div className='fv-help-block color-red'>{String(formik.errors.email)}</div>
            )}
          </div>
        </div>
        {/*End::Form Group email address */}
        {/*Begin::Form Group Move-In Date */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Move-In Date</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter move-in date'
            ></i>
          </label>
          <DatePicker
            autoComplete='off'
            selected={moveInDate}
            dateFormat='yyyy/MM/dd'
            placeholderText='Enter move-in date'
            onChange={(date) => {
              formik.setFieldTouched('moveInDate', true, true)
              if (date === null) {
                setMoveInDate(null)
                formik.setFieldValue('moveInDate', '')
              } else {
                setMoveInDate(date)
                formik.setFieldValue('moveInDate', formatDate(date))
              }
            }}
            className='form-control form-control-lg form-control-solid'
          />
          <div className='h-20px'>
            {formik.touched.moveInDate && formik.errors.moveInDate && (
              <div className='fv-help-block color-red'>{formik.errors.moveInDate}</div>
            )}
          </div>
        </div>
        {/*End::Form Group Move-In Date */}
        {/*Begin::Form Group Rent Agreement Signing Date */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>Rental Agreement Signing Date</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter rental agreement signing date'
            ></i>
          </label>
          <DatePicker
            autoComplete='off'
            selected={rentAgreementSigningDate}
            dateFormat='yyyy/MM/dd'
            placeholderText='Enter rental agreement signing date'
            onChange={(date) => {
              formik.setFieldTouched('rentAgreementSigningDate', true, true)
              if (date === null) {
                setRentAgreementSigningDate(null)
                formik.setFieldValue('rentAgreementSigningDate', '')
              } else {
                setRentAgreementSigningDate(date)
                formik.setFieldValue('rentAgreementSigningDate', formatDate(date))
              }
            }}
            className='form-control form-control-lg form-control-solid'
          />
          <div className='h-20px'>
            {formik.touched.rentAgreementSigningDate && formik.errors.rentAgreementSigningDate && (
              <div className='fv-help-block color-red'>
                {formik.errors.rentAgreementSigningDate}
              </div>
            )}
          </div>
        </div>
        {/*End::Form Group Rent Agreement Signing Date */}
        {/*Begin::Form Group deposit amount */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>Deposit Amount</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter deposit amount'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter deposit amount'
            {...formik.getFieldProps('depositAmount')}
            name='depositAmount'
          />
          <div className='h-20px'>
            {formik.touched.depositAmount && formik.errors.depositAmount && (
              <div className='fv-help-block color-red'>{formik.errors.depositAmount}</div>
            )}
          </div>
        </div>
        {/*End::Form Group deposit amount */}
        {/*Begin::Form Group Monthly Rent */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>Monthly Rent</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter monthly rent'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter monthly rent'
            {...formik.getFieldProps('monthlyRent')}
            name='monthlyRent'
          />
          <div className='h-20px'>
            {formik.touched.monthlyRent && formik.errors.monthlyRent && (
              <div className='fv-help-block color-red'>{formik.errors.monthlyRent}</div>
            )}
          </div>
        </div>
        {/*End::Form Group Monthly Rent */}
        {/*Begin::Form Group Address Line 1 */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>Address Line 1</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter complete address'
            ></i>
          </label>
          <textarea
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter address'
            {...formik.getFieldProps('addressLine1')}
            name='addressLine1'
          />
          <div className='h-20px'>
            {formik.touched.addressLine1 && formik.errors.addressLine1 && (
              <div className='fv-help-block color-red'>{String(formik.errors.addressLine1)}</div>
            )}
          </div>
        </div>
        {/*end::Form Group Address Line 1 */}
        {/*Begin::Form Group Address Line 2*/}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Address Line 2</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter complete address'
            ></i>
          </label>
          <textarea
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter address'
            {...formik.getFieldProps('addressLine2')}
            name='addressLine2'
          />
          <div className='h-20px'>
            {formik.touched.addressLine2 && formik.errors.addressLine2 && (
              <div className='fv-help-block color-red'>{String(formik.errors.addressLine2)}</div>
            )}
          </div>
        </div>
        {/*end::Form Group Address Line 2*/}
        {/*Begin::Form Group city*/}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>City</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter city name'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter city'
            {...formik.getFieldProps('city')}
            name='city'
          />
          <div className='h-20px'>
            {formik.touched.city && formik.errors.city && (
              <div className='fv-help-block color-red'>{String(formik.errors.city)}</div>
            )}
          </div>
        </div>
        {/*end::Form Group city*/}
        {/*Begin::Form Group PIN/Postcode*/}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span className='required'>PIN/Postcode</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter pincode'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter PIN/Postcode'
            {...formik.getFieldProps('pincode')}
            name='pincode'
          />
          <div className='h-20px'>
            {formik.touched.pincode && formik.errors.pincode && (
              <div className='fv-help-block color-red'>{String(formik.errors.pincode)}</div>
            )}
          </div>
        </div>
        {/*End::Form Group PIN/Postcode*/}

        {/*Begin::Form Group Paying NOC Charge*/}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Paying NOC Charge</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Choose whether paying noc charge or not'
            ></i>
          </label>
          <div className='d-flex w-100 gap-10'>
            {/*Begin:Option */}
            <label className='d-flex w-50 flex-fill align-items-center justify-content-between cursor-pointer'>
              <span className='d-flex align-items-center me-2'>
                <span className='symbol symbol-50px me-2'>
                  <span className='symbol-label bg-light-primary'>
                    <i
                      className={`bi bi-check-lg fs-1 ${
                        formik.values.isPayingNOCCharge === 'true' && 'text-primary'
                      }`}
                    ></i>
                  </span>
                </span>

                <span className='d-flex flex-column'>
                  <span className='fw-bolder fs-6'>Yes</span>
                </span>
              </span>

              <span className='form-check form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('isPayingNOCCharge')}
                  name='isPayingNOCCharge'
                  type='radio'
                  value='true'
                  id='isPayingNOCCharge'
                  checked={formik.values.isPayingNOCCharge === 'true'}
                />
              </span>
            </label>
            {/*End::Option */}

            {/*Begin:Option */}
            <label className='d-flex w-50 flex-fill align-items-center justify-content-between cursor-pointer'>
              <span className='d-flex align-items-center me-2'>
                <span className='symbol symbol-50px me-2'>
                  <span className='symbol-label bg-light-danger'>
                    <i
                      className={`bi bi-x-lg fs-2 ${
                        formik.values.isPayingNOCCharge === 'false' && 'text-primary'
                      }`}
                    ></i>
                  </span>
                </span>

                <span className='d-flex flex-column'>
                  <span className='fw-bolder fs-6'>No</span>
                </span>
              </span>

              <span className='form-check form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('isPayingNOCCharge')}
                  name='isPayingNOCCharge'
                  type='radio'
                  value='false'
                  id='isPayingNOCCharge'
                  checked={formik.values.isPayingNOCCharge === 'false'}
                  onChange={(e) => {
                    formik.handleChange(e)
                    if (e.target.value === 'false') {
                      formik.setFieldValue('NOCCharge', '')
                    }
                  }}
                />
              </span>
            </label>
            {/*End::Option */}
          </div>
          <div className='h-20px'>
            {formik.touched.isPayingNOCCharge && formik.errors.isPayingNOCCharge && (
              <div className='fv-help-block color-red'>{formik.errors.isPayingNOCCharge}</div>
            )}
          </div>
        </div>
        {/*End::Form Group Paying NOC Charge*/}

        {/*Begin::Form Group Paying Legal Charge*/}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Paying Legal Charge</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Choose paying legal charge'
            ></i>
          </label>
          <div className='d-flex w-100 gap-10'>
            {/*Begin:Option */}
            <label className='d-flex w-50 flex-fill align-items-center justify-content-between cursor-pointer'>
              <span className='d-flex align-items-center me-2'>
                <span className='symbol symbol-50px me-2'>
                  <span className='symbol-label bg-light-primary'>
                    <i
                      className={`bi bi-check-lg fs-1 ${
                        formik.values.isPayingLegalCharge === 'true' && 'text-primary'
                      }`}
                    ></i>
                  </span>
                </span>

                <span className='d-flex flex-column'>
                  <span className='fw-bolder fs-6'>Yes</span>
                </span>
              </span>

              <span className='form-check form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('isPayingLegalCharge')}
                  name='isPayingLegalCharge'
                  type='radio'
                  value='true'
                  id='isPayingLegalCharge'
                  checked={formik.values.isPayingLegalCharge === 'true'}
                />
              </span>
            </label>
            {/*End::Option */}

            {/*Begin:Option */}
            <label className='d-flex w-50 flex-fill align-items-center justify-content-between cursor-pointer'>
              <span className='d-flex align-items-center me-2'>
                <span className='symbol symbol-50px me-2'>
                  <span className='symbol-label bg-light-danger'>
                    <i
                      className={`bi bi-x-lg fs-2 ${
                        formik.values.isPayingLegalCharge === 'false' && 'text-primary'
                      }`}
                    ></i>
                  </span>
                </span>

                <span className='d-flex flex-column'>
                  <span className='fw-bolder fs-6'>No</span>
                </span>
              </span>

              <span className='form-check form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('isPayingLegalCharge')}
                  name='isPayingLegalCharge'
                  type='radio'
                  value='false'
                  id='isPayingLegalCharge'
                  checked={formik.values.isPayingLegalCharge === 'false'}
                  onChange={(e) => {
                    formik.handleChange(e) // Handle form change
                    if (e.target.value === 'false') {
                      formik.setFieldValue('legalCharge', '') // Clear legalCharge when false is selected
                    }
                  }}
                />
              </span>
            </label>
            {/*End::Option */}
          </div>
          <div className='h-20px'>
            {formik.touched.isPayingLegalCharge && formik.errors.isPayingLegalCharge && (
              <div className='fv-help-block color-red'>{formik.errors.isPayingLegalCharge}</div>
            )}
          </div>
        </div>
        {/*End::Form Group Paying Legal Charge*/}

        {/*Begin::Form NOC Charge */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>NOC Charge</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter NOC Charge'
            ></i>
          </label>
          <input
            type='number'
            className={`form-control form-control-lg ${
              !formik.values.isPayingNOCCharge || formik.values.isPayingNOCCharge === 'false'
                ? ''
                : 'form-control-solid'
            }`}
            placeholder='Enter NOC Charge'
            {...formik.getFieldProps('NOCCharge')}
            name='NOCCharge'
            disabled={
              !formik.values.isPayingNOCCharge || formik.values.isPayingNOCCharge === 'false'
            }
          />
          <div className='h-20px'>
            {formik.touched.NOCCharge && formik.errors.NOCCharge && (
              <div className='fv-help-block color-red'>{formik.errors.NOCCharge}</div>
            )}
          </div>
        </div>
        {/*end::Form NOC Charge */}

        {/*Begin::Form LegalCharge Charge */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Legal Charge</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter Legal Charge'
            ></i>
          </label>
          <input
            type='number'
            className={`form-control form-control-lg ${
              !formik.values.isPayingLegalCharge || formik.values.isPayingLegalCharge === 'false'
                ? ''
                : 'form-control-solid'
            }`}
            placeholder='Enter Legal Charge'
            {...formik.getFieldProps('legalCharge')}
            name='legalCharge'
            disabled={
              !formik.values.isPayingLegalCharge || formik.values.isPayingLegalCharge === 'false'
            }
          />
          <div className='h-20px'>
            {formik.touched.legalCharge && formik.errors.legalCharge && (
              <div className='fv-help-block color-red'>{formik.errors.legalCharge}</div>
            )}
          </div>
        </div>
        {/*end::Form LegalCharge Charge */}

        {/*Begin::Form AdministrationCharge Charge */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Administration Charge</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter Administration Charge'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter Administration Charge'
            {...formik.getFieldProps('administrationCharges')}
            name='administrationCharges'
          />
          <div className='h-20px'>
            {formik.touched.administrationCharges && formik.errors.administrationCharges && (
              <div className='fv-help-block color-red'>{formik.errors.administrationCharges}</div>
            )}
          </div>
        </div>
        {/*end::Form AdministrationCharge Charge */}

        {/*Begin::Form Brokerage Cost Charge */}
        <div className='col-12 col-lg-6 mb-2'>
          <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
            <span>Brokerage Cost</span>
            <i
              className='fas fa-exclamation-circle ms-2 fs-7'
              data-bs-toggle='tooltip'
              title='Enter Brokerage Cost'
            ></i>
          </label>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            placeholder='Enter Brokerage Cost'
            {...formik.getFieldProps('brokerageCost')}
            name='brokerageCost'
          />
          <div className='h-20px'>
            {formik.touched.brokerageCost && formik.errors.brokerageCost && (
              <div className='fv-help-block color-red'>{formik.errors.brokerageCost}</div>
            )}
          </div>
        </div>
        {/*end::Form LegalCharge Charge */}

        {/* Begin: AAdhar card upload section */}
        <AadharCardUpload
          formik={formik}
          uploadAadharCard={uploadAadharCard}
          setUploadAadharCard={setUploadAadharCard}
          aadharDetails={aadharDetails}
          setAadharDetails={setAadharDetails}
        />
        {/* End: AAdhar card upload section */}

        {/* Begin: Company ID upload section */}
        <CompanyIDUpload
          formik={formik}
          uploadCompanyID={uploadCompanyID}
          setUploadCompanyID={setUploadCompanyID}
          companyIDDetails={companyIDDetails}
          setCompanyIDDetails={setCompanyIDDetails}
        />
        {/* End: Company ID upload section */}
      </div>
      {/* End: form fields */}

      <div className='d-flex justify-content-center mb-4'>
        {/* Begin:: submit button */}
        <button
          type='submit'
          className='btn btn-lg btn-primary mt-5'
          // data-kt-stepper-action='submit'
          disabled={submitBtnLoading}
        >
          {submitBtnLoading ? (
            <span className='indicator-progress' style={{display: 'block'}}>
              Processing...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          ) : (
            'Submit'
          )}
        </button>
        {/* Begin:: submit button */}
      </div>
    </form>
  )
}

export default LeadsDetailsForm
